import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  ArrowLeft, Search, Calendar, RefreshCw, Edit, Trash2, 
  FileText, X, Package, Loader2, ChevronDown, Image as ImageIcon
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { toast } from 'sonner';
import apiClient from '@/services/apiClient';

const InwardReports = () => {
  const navigate = useNavigate();
  const observerRef = useRef(null);
  const loadMoreRef = useRef(null);

  // Data state
  const [entries, setEntries] = useState([]);
  const [total, setTotal] = useState(0);
  const [hasMore, setHasMore] = useState(true);
  const [skip, setSkip] = useState(0);
  const limit = 30;

  // Sections for dropdown
  const [sections, setSections] = useState([]);

  // Filter state
  const [search, setSearch] = useState('');
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  const searchTimeoutRef = useRef(null);

  // UI state
  const [isLoading, setIsLoading] = useState(true);
  const [isLoadingMore, setIsLoadingMore] = useState(false);

  // Edit modal state
  const [editEntry, setEditEntry] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [isSaving, setIsSaving] = useState(false);

  // Image viewer state
  const [imageViewerEntry, setImageViewerEntry] = useState(null);
  const [signedImageUrls, setSignedImageUrls] = useState([]);
  const [isLoadingImages, setIsLoadingImages] = useState(false);

  // Bundle popup state
  const [bundlePopup, setBundlePopup] = useState(null);
  const [isDeletingBundle, setIsDeletingBundle] = useState(false);

  // Delete dialog state
  const [deleteEntry, setDeleteEntry] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);

  // Move to draft dialog state
  const [moveToDraftEntry, setMoveToDraftEntry] = useState(null);
  const [isMovingToDraft, setIsMovingToDraft] = useState(false);

  const fetchEntries = useCallback(async (reset = false) => {
    const currentSkip = reset ? 0 : skip;
    if (reset) {
      setIsLoading(true);
    } else {
      setIsLoadingMore(true);
    }

    try {
      const params = new URLSearchParams();
      params.append('skip', currentSkip.toString());
      params.append('limit', limit.toString());
      if (search) params.append('search', search);
      if (fromDate) params.append('from_date', fromDate);
      if (toDate) params.append('to_date', toDate);

      const response = await apiClient.get(`/inward/reports?${params.toString()}`);

      const newEntries = response.data.entries || [];
      
      if (reset) {
        setEntries(newEntries);
        setSkip(limit);
      } else {
        setEntries(prev => [...prev, ...newEntries]);
        setSkip(currentSkip + limit);
      }
      
      setTotal(response.data.total);
      setHasMore(response.data.has_more);
    } catch (error) {
      console.error('Error fetching entries:', error);
      toast.error('Failed to load entries');
    } finally {
      setIsLoading(false);
      setIsLoadingMore(false);
    }
  }, [skip, search, fromDate, toDate]);

  // Initial load
  useEffect(() => {
    fetchEntries(true);
    fetchSections();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchSections = async () => {
    try {
      const response = await apiClient.get('/inward/sections');
      setSections(response.data.sections || []);
    } catch (error) {
      console.error('Error fetching sections:', error);
    }
  };

  // Debounced search
  useEffect(() => {
    if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);
    searchTimeoutRef.current = setTimeout(() => {
      fetchEntries(true);
    }, 500);
    return () => {
      if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search, fromDate, toDate]);

  // Infinite scroll observer
  useEffect(() => {
    if (observerRef.current) observerRef.current.disconnect();

    observerRef.current = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && hasMore && !isLoadingMore && !isLoading) {
          fetchEntries(false);
        }
      },
      { threshold: 0.1 }
    );

    if (loadMoreRef.current) {
      observerRef.current.observe(loadMoreRef.current);
    }

    return () => {
      if (observerRef.current) observerRef.current.disconnect();
    };
  }, [hasMore, isLoadingMore, isLoading, fetchEntries]);

  const formatDateTime = (timestamp) => {
    if (!timestamp) return '-';
    try {
      if (timestamp.includes('T')) {
        const date = new Date(timestamp);
        return date.toLocaleString('en-IN', {
          day: '2-digit',
          month: '2-digit',
          year: 'numeric',
          hour: '2-digit',
          minute: '2-digit',
          hour12: false
        });
      }
      return timestamp;
    } catch {
      return timestamp;
    }
  };

  // Edit handlers
  const handleEdit = (entry) => {
    setEditEntry(entry);
    setEditForm({
      section_name: entry.section_name || '',
      vendor_name: entry.vendor_name || '',
      vendor_doc_no: entry.vendor_doc_no || '',
      vendor_doc_date: entry.vendor_doc_date || '',
      invoice_qty: entry.invoice_qty || '',
      invoice_value: entry.invoice_value || '',
      vehicle_no: entry.vehicle_no || ''
    });
  };

  // Image viewer handler
  const handleViewImages = async (entry) => {
    if (!entry.image_urls || entry.image_urls.length === 0) {
      toast.info('No images available for this entry');
      return;
    }
    
    setImageViewerEntry(entry);
    setIsLoadingImages(true);
    setSignedImageUrls([]);
    
    try {
      const response = await apiClient.post(`/inward/entries/${entry.entry_id}/signed-urls`);
      setSignedImageUrls(response.data.signed_urls || []);
    } catch (error) {
      console.error('Error fetching signed URLs:', error);
      toast.error('Failed to load images');
    } finally {
      setIsLoadingImages(false);
    }
  };

  const handleSaveEdit = async () => {
    setIsSaving(true);
    try {
      const updateData = {
        section_name: editForm.section_name || null,
        vendor_name: editForm.vendor_name || null,
        vendor_doc_no: editForm.vendor_doc_no || null,
        vendor_doc_date: editForm.vendor_doc_date || null,
        invoice_qty: editForm.invoice_qty ? parseFloat(editForm.invoice_qty) : null,
        invoice_value: editForm.invoice_value ? parseFloat(editForm.invoice_value) : null,
        vehicle_no: editForm.vehicle_no || null
      };

      await apiClient.put(`/inward/entries/${editEntry.entry_id}`, updateData);

      // Update local state
      setEntries(entries.map(e => 
        e.entry_id === editEntry.entry_id 
          ? { ...e, ...updateData }
          : e
      ));

      toast.success('Entry updated successfully');
      setEditEntry(null);
    } catch (error) {
      console.error('Error updating entry:', error);
      toast.error('Failed to update entry');
    } finally {
      setIsSaving(false);
    }
  };

  // Delete handler
  const handleDelete = async () => {
    if (!deleteEntry) return;
    setIsDeleting(true);
    try {
      await apiClient.delete(`/inward/entries/${deleteEntry.entry_id}`);
      setEntries(entries.filter(e => e.entry_id !== deleteEntry.entry_id));
      setTotal(total - 1);
      toast.success('Entry deleted successfully');
    } catch (error) {
      console.error('Error deleting entry:', error);
      toast.error('Failed to delete entry');
    } finally {
      setIsDeleting(false);
      setDeleteEntry(null);
    }
  };

  // Move to draft handler
  const handleMoveToDraft = async () => {
    if (!moveToDraftEntry) return;
    setIsMovingToDraft(true);
    try {
      await apiClient.put(`/inward/entries/${moveToDraftEntry.entry_id}/move-to-draft`);
      setEntries(entries.filter(e => e.entry_id !== moveToDraftEntry.entry_id));
      setTotal(total - 1);
      toast.success('Entry moved to drafts');
    } catch (error) {
      console.error('Error moving to draft:', error);
      toast.error('Failed to move entry to draft');
    } finally {
      setIsMovingToDraft(false);
      setMoveToDraftEntry(null);
    }
  };

  // Bundle delete handler
  const handleDeleteBundle = async (bundleBarcode) => {
    if (!bundlePopup) return;
    setIsDeletingBundle(true);
    try {
      const encodedBarcode = encodeURIComponent(bundleBarcode);
      await apiClient.delete(`/inward/entries/${bundlePopup.entry_id}/bundles/${encodedBarcode}`);
      
      // Update local state
      const updatedBundles = bundlePopup.bundles.filter(b => b.bundle_barcode !== bundleBarcode);
      setBundlePopup({ ...bundlePopup, bundles: updatedBundles });
      setEntries(entries.map(e => 
        e.entry_id === bundlePopup.entry_id 
          ? { ...e, bundles: updatedBundles }
          : e
      ));
      
      toast.success('Bundle deleted');
    } catch (error) {
      console.error('Error deleting bundle:', error);
      toast.error('Failed to delete bundle');
    } finally {
      setIsDeletingBundle(false);
    }
  };

  const clearFilters = () => {
    setSearch('');
    setFromDate('');
    setToDate('');
  };

  return (
    <div className="min-h-screen bg-[#FFFBF5]">
      {/* Header */}
      <header className="bg-[#1A1F2E] text-[#F9FAFB] shadow-lg sticky top-0 z-40">
        <div className="max-w-full mx-auto px-4 md:px-6 h-16 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button variant="ghost" onClick={() => navigate('/admin/dashboard')} className="text-[#F9FAFB] hover:bg-white/10">
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <div>
              <h1 className="text-xl font-bold font-display">Inward Reports</h1>
              <p className="text-sm text-[#E8DCC8]">View and manage all inward entries</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <span className="text-sm text-[#E8DCC8]">Total: {total}</span>
            <Button 
              onClick={() => fetchEntries(true)} 
              variant="secondary" 
              className="h-10"
              disabled={isLoading}
            >
              <RefreshCw className={`w-4 h-4 ${isLoading ? 'animate-spin' : ''}`} />
            </Button>
          </div>
        </div>
      </header>

      {/* Filters */}
      <div className="bg-white border-b border-[#E5DFD0] sticky top-16 z-30">
        <div className="max-w-full mx-auto px-4 md:px-6 py-4">
          <div className="flex flex-wrap items-end gap-4">
            {/* Search */}
            <div className="flex-1 min-w-[250px]">
              <Label className="text-sm text-[#5A5F6E]">Search</Label>
              <div className="relative mt-1">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#8A8F9E]" />
                <Input
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="Search by entry ID, staff, vendor, vehicle..."
                  className="pl-10"
                />
              </div>
            </div>

            {/* Date Filters */}
            <div className="w-40">
              <Label className="text-sm text-[#5A5F6E]">From Date</Label>
              <Input
                type="date"
                value={fromDate}
                onChange={(e) => setFromDate(e.target.value)}
                className="mt-1"
              />
            </div>
            <div className="w-40">
              <Label className="text-sm text-[#5A5F6E]">To Date</Label>
              <Input
                type="date"
                value={toDate}
                onChange={(e) => setToDate(e.target.value)}
                className="mt-1"
              />
            </div>

            {/* Clear Filters */}
            {(search || fromDate || toDate) && (
              <Button variant="outline" onClick={clearFilters} className="h-10">
                <X className="w-4 h-4 mr-1" />
                Clear
              </Button>
            )}
          </div>
        </div>
      </div>

      {/* Table */}
      <main className="max-w-full mx-auto px-4 md:px-6 py-6">
        <div className="bg-white rounded-lg border border-[#E5DFD0] overflow-hidden">
          {isLoading ? (
            <div className="flex justify-center py-20">
              <RefreshCw className="w-8 h-8 animate-spin text-[#2D5F4F]" />
            </div>
          ) : entries.length === 0 ? (
            <div className="text-center py-20">
              <FileText className="w-12 h-12 text-[#E5DFD0] mx-auto mb-4" />
              <p className="text-[#5A5F6E]">No entries found</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow className="bg-[#1A1F2E] hover:bg-[#1A1F2E]">
                    <TableHead className="text-white font-semibold">SL</TableHead>
                    <TableHead className="text-white font-semibold">Staff ID</TableHead>
                    <TableHead className="text-white font-semibold">Doc Barcode</TableHead>
                    <TableHead className="text-white font-semibold">Timestamp</TableHead>
                    <TableHead className="text-white font-semibold">Bundles</TableHead>
                    <TableHead className="text-white font-semibold">Images</TableHead>
                    <TableHead className="text-white font-semibold">Section</TableHead>
                    <TableHead className="text-white font-semibold">Vendor</TableHead>
                    <TableHead className="text-white font-semibold">Vendor Doc</TableHead>
                    <TableHead className="text-white font-semibold">Doc Date</TableHead>
                    <TableHead className="text-white font-semibold">Qty</TableHead>
                    <TableHead className="text-white font-semibold">Value</TableHead>
                    <TableHead className="text-white font-semibold">Vehicle</TableHead>
                    <TableHead className="text-white font-semibold">Acct Status</TableHead>
                    <TableHead className="text-white font-semibold text-center">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {entries.map((entry, index) => (
                    <TableRow key={entry.entry_id} className="hover:bg-[#FFFBF5]">
                      <TableCell className="font-medium text-[#1A1F2E]">{index + 1}</TableCell>
                      <TableCell className="text-sm text-[#5A5F6E]">{entry.staff_id}</TableCell>
                      <TableCell className="font-mono text-sm text-[#1A1F2E]">{entry.document_barcode}</TableCell>
                      <TableCell className="text-sm text-[#5A5F6E]">{formatDateTime(entry.timestamp)}</TableCell>
                      <TableCell>
                        <button
                          onClick={() => setBundlePopup(entry)}
                          className="text-[#2D5F4F] hover:underline font-medium"
                        >
                          {entry.bundles?.length || 0}
                        </button>
                      </TableCell>
                      <TableCell>
                        {entry.image_urls?.length > 0 ? (
                          <button
                            onClick={() => handleViewImages(entry)}
                            className="text-blue-600 hover:text-blue-800 flex items-center gap-1"
                            title="View Images"
                          >
                            <ImageIcon className="w-4 h-4" />
                            <span className="text-xs">{entry.image_urls.length}</span>
                          </button>
                        ) : (
                          <span className="text-[#8A8F9E]">-</span>
                        )}
                      </TableCell>
                      <TableCell className="text-sm text-[#5A5F6E]">{entry.section_name || '-'}</TableCell>
                      <TableCell className="text-sm text-[#5A5F6E]">{entry.vendor_name || '-'}</TableCell>
                      <TableCell className="text-sm text-[#5A5F6E]">{entry.vendor_doc_no || '-'}</TableCell>
                      <TableCell className="text-sm text-[#5A5F6E]">{entry.vendor_doc_date || '-'}</TableCell>
                      <TableCell className="text-sm text-[#5A5F6E]">{entry.invoice_qty || '-'}</TableCell>
                      <TableCell className="text-sm text-[#5A5F6E]">{entry.invoice_value || '-'}</TableCell>
                      <TableCell className="text-sm text-[#5A5F6E]">{entry.vehicle_no || '-'}</TableCell>
                      <TableCell className="text-sm text-[#5A5F6E]">{entry.account_status || '-'}</TableCell>
                      <TableCell>
                        <div className="flex items-center justify-center gap-1">
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => handleEdit(entry)}
                            className="h-8 w-8 p-0 text-blue-600 hover:bg-blue-50"
                            title="Edit"
                          >
                            <Edit className="w-4 h-4" />
                          </Button>
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => setMoveToDraftEntry(entry)}
                            className="h-8 w-8 p-0 text-orange-600 hover:bg-orange-50"
                            title="Move to Draft"
                          >
                            <FileText className="w-4 h-4" />
                          </Button>
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => setDeleteEntry(entry)}
                            className="h-8 w-8 p-0 text-red-600 hover:bg-red-50"
                            title="Delete"
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}

          {/* Load More Trigger */}
          {hasMore && !isLoading && (
            <div ref={loadMoreRef} className="flex justify-center py-4">
              {isLoadingMore && (
                <div className="flex items-center gap-2 text-[#5A5F6E]">
                  <Loader2 className="w-5 h-5 animate-spin" />
                  <span>Loading more...</span>
                </div>
              )}
            </div>
          )}
        </div>
      </main>

      {/* Edit Modal */}
      <Dialog open={!!editEntry} onOpenChange={() => setEditEntry(null)}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Edit Entry: {editEntry?.entry_id}</DialogTitle>
            <DialogDescription>
              Update the editable fields for this entry.
            </DialogDescription>
          </DialogHeader>
          <div className="grid grid-cols-2 gap-4 py-4">
            <div>
              <Label>Section Name</Label>
              <Select
                value={editForm.section_name || 'none'}
                onValueChange={(value) => setEditForm({ ...editForm, section_name: value === 'none' ? '' : value })}
              >
                <SelectTrigger className="mt-1">
                  <SelectValue placeholder="Select section" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">-- Select Section --</SelectItem>
                  {sections.map((section) => (
                    <SelectItem key={section.section_id || section.section_name} value={section.section_name}>
                      {section.section_name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Vendor Name</Label>
              <Input
                value={editForm.vendor_name || ''}
                onChange={(e) => setEditForm({ ...editForm, vendor_name: e.target.value })}
                className="mt-1"
              />
            </div>
            <div>
              <Label>Vendor Doc No</Label>
              <Input
                value={editForm.vendor_doc_no || ''}
                onChange={(e) => setEditForm({ ...editForm, vendor_doc_no: e.target.value })}
                className="mt-1"
              />
            </div>
            <div>
              <Label>Vendor Doc Date</Label>
              <Input
                type="date"
                value={editForm.vendor_doc_date || ''}
                onChange={(e) => setEditForm({ ...editForm, vendor_doc_date: e.target.value })}
                className="mt-1"
              />
            </div>
            <div>
              <Label>Invoice Qty</Label>
              <Input
                type="number"
                value={editForm.invoice_qty || ''}
                onChange={(e) => setEditForm({ ...editForm, invoice_qty: e.target.value })}
                className="mt-1"
              />
            </div>
            <div>
              <Label>Invoice Value</Label>
              <Input
                type="number"
                value={editForm.invoice_value || ''}
                onChange={(e) => setEditForm({ ...editForm, invoice_value: e.target.value })}
                className="mt-1"
              />
            </div>
            <div className="col-span-2">
              <Label>Vehicle No</Label>
              <Input
                value={editForm.vehicle_no || ''}
                onChange={(e) => setEditForm({ ...editForm, vehicle_no: e.target.value })}
                className="mt-1"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditEntry(null)} disabled={isSaving}>
              Cancel
            </Button>
            <Button onClick={handleSaveEdit} disabled={isSaving} className="bg-[#2D5F4F] hover:bg-[#234A3D]">
              {isSaving ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Saving...</> : 'Save Changes'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Bundle Popup */}
      <Dialog open={!!bundlePopup} onOpenChange={() => setBundlePopup(null)}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Package className="w-5 h-5" />
              Bundles for {bundlePopup?.document_barcode}
            </DialogTitle>
            <DialogDescription>
              {bundlePopup?.bundles?.length || 0} bundle(s) scanned
            </DialogDescription>
          </DialogHeader>
          <div className="max-h-96 overflow-y-auto">
            {bundlePopup?.bundles?.length === 0 ? (
              <p className="text-center text-[#5A5F6E] py-8">No bundles</p>
            ) : (
              <div className="space-y-2">
                {bundlePopup?.bundles?.map((bundle, idx) => (
                  <div 
                    key={bundle.bundle_barcode} 
                    className="flex items-center justify-between p-3 bg-[#F5F1E8] rounded-lg"
                  >
                    <div>
                      <p className="font-mono font-medium text-[#1A1F2E]">{bundle.bundle_barcode}</p>
                      <p className="text-xs text-[#5A5F6E]">{formatDateTime(bundle.timestamp)}</p>
                    </div>
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => handleDeleteBundle(bundle.bundle_barcode)}
                      disabled={isDeletingBundle}
                      className="text-red-600 hover:bg-red-50"
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                ))}
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation */}
      <AlertDialog open={!!deleteEntry} onOpenChange={() => setDeleteEntry(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Entry?</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to permanently delete entry "{deleteEntry?.entry_id}"? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isDeleting}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              disabled={isDeleting}
              className="bg-red-600 hover:bg-red-700"
            >
              {isDeleting ? 'Deleting...' : 'Delete'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Move to Draft Confirmation */}
      <AlertDialog open={!!moveToDraftEntry} onOpenChange={() => setMoveToDraftEntry(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Move to Draft?</AlertDialogTitle>
            <AlertDialogDescription>
              This will move entry "{moveToDraftEntry?.entry_id}" back to draft status. The entry will appear in the Drafts section.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isMovingToDraft}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleMoveToDraft}
              disabled={isMovingToDraft}
              className="bg-orange-600 hover:bg-orange-700"
            >
              {isMovingToDraft ? 'Moving...' : 'Move to Draft'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Image Viewer Dialog */}
      <Dialog open={!!imageViewerEntry} onOpenChange={() => setImageViewerEntry(null)}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <ImageIcon className="w-5 h-5" />
              Document Images - {imageViewerEntry?.document_barcode}
            </DialogTitle>
            <DialogDescription>
              {imageViewerEntry?.image_urls?.length || 0} image(s) uploaded
            </DialogDescription>
          </DialogHeader>
          <div className="py-4">
            {isLoadingImages ? (
              <div className="flex justify-center py-12">
                <Loader2 className="w-8 h-8 animate-spin text-[#2D5F4F]" />
              </div>
            ) : signedImageUrls.length === 0 ? (
              <p className="text-center text-[#5A5F6E] py-8">No images available</p>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {signedImageUrls.map((url, idx) => (
                  url && (
                    <div key={idx} className="border border-[#E5DFD0] rounded-lg overflow-hidden">
                      <img 
                        src={url} 
                        alt={`Document ${idx + 1}`}
                        className="w-full h-auto object-contain max-h-80"
                        onError={(e) => {
                          e.target.style.display = 'none';
                        }}
                      />
                      <div className="p-2 bg-[#F5F1E8] text-center">
                        <a 
                          href={url} 
                          target="_blank" 
                          rel="noopener noreferrer"
                          className="text-sm text-[#2D5F4F] hover:underline"
                        >
                          Open in new tab
                        </a>
                      </div>
                    </div>
                  )
                ))}
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default InwardReports;
